import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

function Settings({ token }) {
  const toast = useToast();
  const [tab, setTab] = useState('api-keys');
  const [apiKeys, setApiKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [sub, setSub] = useState(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/api-keys`, { headers });
      if (res.ok) setApiKeys((await res.json()).keys || []);
    } catch (e) {}
  };

  const fetchSub = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/subscription`, { headers });
      if (res.ok) setSub(await res.json());
    } catch (e) {}
  };

  useEffect(() => { fetchKeys(); fetchSub(); }, [token]);

  const createKey = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/api-keys`, { method: 'POST', headers, body: '{}' });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to create key'));
      setNewKey(await res.json());
      fetchKeys();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const deleteKey = async (id) => {
    if (!window.confirm('Delete this API key?')) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/api-keys/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to delete key'));
      toast('API key deleted', 'success');
      fetchKeys();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const handleUpgrade = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/subscription/checkout`, { method: 'POST', headers, body: '{}' });
      if (!res.ok) throw new Error(await parseApiError(res, 'Checkout not available'));
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription?')) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/subscription`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to cancel subscription'));
      toast('Subscription cancelled', 'success');
      fetchSub();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const isFree = !sub || sub.tier === 'free';
  const isActive = sub && (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due');

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">API keys, billing, and SDK integration</div>
        </div>
      </div>
      <div className="page-body">
        <div className="tabs">
          <button className={`tab-btn ${tab === 'api-keys' ? 'active' : ''}`} onClick={() => setTab('api-keys')}>API Keys</button>
          <button className={`tab-btn ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}>Billing</button>
          <button className={`tab-btn ${tab === 'sdk' ? 'active' : ''}`} onClick={() => setTab('sdk')}>SDK Setup</button>
        </div>

        {tab === 'api-keys' && (
          <div className="card">
            <div className="card-header">
              <div className="card-title">Platform API Keys</div>
              <button className="btn-primary btn-sm" onClick={createKey}>Create Key</button>
            </div>

            {newKey && (
              <div className="alert success" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: 16 }}>
                <strong>New key created!</strong>
                <div className="code-block" style={{ marginTop: 8, width: '100%', wordBreak: 'break-all' }}>{newKey.key}</div>
                <div className="text-xs" style={{ marginTop: 4 }}>Save this key now. You won't see it again.</div>
              </div>
            )}

            {apiKeys.length === 0 ? (
              <div className="text-sm text-muted" style={{ padding: 20, textAlign: 'center' }}>No API keys created yet.</div>
            ) : (
              <table>
                <thead><tr><th>Key</th><th>Created</th><th>Last Used</th><th></th></tr></thead>
                <tbody>
                  {apiKeys.map(key => (
                    <tr key={key.id}>
                      <td className="text-mono text-sm">{key.key_preview}</td>
                      <td className="text-sm">{new Date(key.created_at).toLocaleDateString()}</td>
                      <td className="text-sm">{key.last_used ? new Date(key.last_used).toLocaleDateString() : 'Never'}</td>
                      <td><button className="btn-danger btn-sm" onClick={() => deleteKey(key.id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'billing' && (
          <div style={{ maxWidth: 500 }}>
            <div className="card">
              {isFree ? (
                <>
                  <div className="card-title" style={{ marginBottom: 16 }}>Free Plan</div>
                  <p className="text-sm text-secondary" style={{ marginBottom: 16 }}>Upgrade to Pro to unlock all features.</p>
                  <div style={{ background: 'var(--bg-primary)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 16 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>$5<span className="text-sm text-muted">/month</span></div>
                    <div className="text-xs text-muted" style={{ marginBottom: 16 }}>Everything included. No hidden fees.</div>
                    {['Unlimited agents & traces', 'Real-time monitoring', 'API key vault', 'Governance policies', 'Simulation lab', 'Prompt versioning', 'Quality alerts', 'Team collaboration'].map(f => (
                      <div key={f} style={{ padding: '4px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--success)', marginRight: 8 }}>&#10003;</span>{f}
                      </div>
                    ))}
                  </div>
                  <button className="btn-primary w-full" onClick={handleUpgrade}>Start 7-Day Free Trial</button>
                  <div className="text-xs text-muted text-center" style={{ marginTop: 8 }}>No charge for 7 days. Cancel anytime.</div>
                </>
              ) : (
                <>
                  <div className="card-title" style={{ marginBottom: 12 }}>Pro Plan</div>
                  <div style={{ marginBottom: 8 }}>
                    <span className={`badge ${isActive ? 'badge-success' : 'badge-error'}`}>{sub.status}</span>
                  </div>
                  {sub.trial_end && <div className="text-sm text-secondary" style={{ marginBottom: 4 }}>Trial ends: {new Date(sub.trial_end).toLocaleDateString()}</div>}
                  {sub.current_period_end && <div className="text-sm text-secondary" style={{ marginBottom: 16 }}>Next billing: {new Date(sub.current_period_end).toLocaleDateString()}</div>}
                  {isActive && <button className="btn-danger" onClick={handleCancel}>Cancel Subscription</button>}
                </>
              )}
            </div>
          </div>
        )}

        {tab === 'sdk' && (
          <>
            <div className="card">
              <div className="card-title" style={{ marginBottom: 16 }}>Quick Start</div>
              <p className="text-sm text-secondary" style={{ marginBottom: 16 }}>
                Send events and traces to Agent Oversight from any language. Here are examples for the most common setups.
              </p>

              <div style={{ marginBottom: 24 }}>
                <div className="text-xs text-muted mb-2" style={{ fontWeight: 600 }}>1. Report an Event (curl)</div>
                <div className="code-block">{`curl -X POST ${API_URL}/api/agents/events \\
  -H "X-Agent-Key: YOUR_AGENT_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"action_type":"web_search","target":"latest AI news","result":"success"}'`}</div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div className="text-xs text-muted mb-2" style={{ fontWeight: 600 }}>2. Send a Heartbeat (curl)</div>
                <div className="code-block">{`curl -X PUT ${API_URL}/api/agents/heartbeat \\
  -H "X-Agent-Key: YOUR_AGENT_KEY"`}</div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div className="text-xs text-muted mb-2" style={{ fontWeight: 600 }}>3. Ingest a Trace (curl)</div>
                <div className="code-block">{`curl -X POST ${API_URL}/api/agents/traces \\
  -H "X-Agent-Key: YOUR_AGENT_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
  "trace_id": "tr_unique_id",
  "name": "user-query",
  "status": "completed",
  "spans": [{
    "span_id": "sp_1",
    "name": "llm-call",
    "type": "llm",
    "status": "completed",
    "started_at": "2026-02-24T00:00:00Z",
    "ended_at": "2026-02-24T00:00:01Z",
    "tokens_in": 150,
    "tokens_out": 300,
    "cost": 0.002,
    "input": "What is AI?",
    "output": "AI is..."
  }]
}'`}</div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div className="text-xs text-muted mb-2" style={{ fontWeight: 600 }}>4. JavaScript SDK</div>
                <div className="code-block">{`// agent-oversight.js — Minimal SDK
const AO_URL = '${API_URL}';

class AgentOversight {
  constructor(agentKey) {
    this.key = agentKey;
    this.headers = {
      'X-Agent-Key': agentKey,
      'Content-Type': 'application/json',
    };
  }

  async event(actionType, target, result = 'success', metadata = {}) {
    await fetch(AO_URL + '/api/agents/events', {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ action_type: actionType, target, result, metadata }),
    });
  }

  async heartbeat() {
    await fetch(AO_URL + '/api/agents/heartbeat', {
      method: 'PUT', headers: this.headers,
    });
  }

  async trace(traceId, name, spans, status = 'completed') {
    await fetch(AO_URL + '/api/agents/traces', {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ trace_id: traceId, name, status, spans }),
    });
  }
}

// Usage:
const ao = new AgentOversight('ao_agent_YOUR_KEY');
ao.heartbeat();
ao.event('web_search', 'latest AI news');
ao.trace('tr_123', 'user-query', [{
  span_id: 'sp_1', name: 'llm-call', type: 'llm',
  status: 'completed', tokens_in: 100, tokens_out: 200,
}]);`}</div>
              </div>

              <div>
                <div className="text-xs text-muted mb-2" style={{ fontWeight: 600 }}>5. Python SDK</div>
                <div className="code-block">{`# agent_oversight.py — Minimal SDK
import requests

class AgentOversight:
    def __init__(self, agent_key):
        self.url = '${API_URL}'
        self.headers = {
            'X-Agent-Key': agent_key,
            'Content-Type': 'application/json',
        }

    def event(self, action_type, target='', result='success', metadata=None):
        requests.post(f'{self.url}/api/agents/events', json={
            'action_type': action_type, 'target': target,
            'result': result, 'metadata': metadata or {},
        }, headers=self.headers)

    def heartbeat(self):
        requests.put(f'{self.url}/api/agents/heartbeat', headers=self.headers)

    def trace(self, trace_id, name, spans, status='completed'):
        requests.post(f'{self.url}/api/agents/traces', json={
            'trace_id': trace_id, 'name': name,
            'status': status, 'spans': spans,
        }, headers=self.headers)

# Usage:
ao = AgentOversight('ao_agent_YOUR_KEY')
ao.heartbeat()
ao.event('web_search', 'latest AI news')
ao.trace('tr_123', 'user-query', [{
    'span_id': 'sp_1', 'name': 'llm-call', 'type': 'llm',
    'status': 'completed', 'tokens_in': 100, 'tokens_out': 200,
}])`}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Settings;
